import { HttpClient } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { Identity } from '@ppl/environment';
import { BehaviorSubject, of } from 'rxjs';
import type { Observable } from 'rxjs';

export interface Icon {
  dataURL: string;
  innerHTML: string;
}

export interface Icons {
  [id: string]: Icon;
}

export class IconCacheService {
  setIcons(icons: Icons) {
  }

  getIcons(): Observable<Icons> {
    return of(null);
  }
}

@Injectable()
export class PplIconService {

  icons: { [id: string]: Icon; } = {};
  isLoaded$ = new BehaviorSubject(false);

  constructor(
    private httpClient: HttpClient,
    @Optional() private iconCache: IconCacheService) {
  }

  registerPplIcons(url: string, hasCoeveraIcons = false) {
    if (!this.iconCache) {
      this.fetchIcons(url, hasCoeveraIcons);
      return;
    }

    this.iconCache.getIcons().subscribe(icons => {
      if (icons) {
        this.icons = icons;
        this.isLoaded$.next(true);
      } else {
        this.fetchIcons(url, hasCoeveraIcons);
      }
    }, () => this.fetchIcons(url, hasCoeveraIcons));
  }

  setPplIcons(innerHTML: string) {
    this.parseIcons(innerHTML);

    this.isLoaded$.next(true);
  }

  private fetchIcons(url: string, hasCoeveraIcons: boolean) {
    this.httpClient.get(`${url}/assets/${(Identity === 'coevera' && hasCoeveraIcons) ? 'icons-coevera' : 'icons'}.svg`, {
      responseType: 'text'
    }).subscribe(response => {
      this.parseIcons(response);

      if (this.iconCache) {
        this.iconCache.setIcons(this.icons);
      }

      this.isLoaded$.next(true);
    });
    return;
  }

  private parseIcons(response: string) {
    for (const row of response.split('\n')) {
      if (!row.startsWith('<svg')) {
        continue;
      }

      const iconName = row.match(/class="(.+?)"/)[1];

      const innerHTML = row.includes('data-source="lucide"')
        ? `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"` + row.slice(4)
        : row;

      this.icons[iconName] = {
        innerHTML,
        dataURL: svgToDataURL(innerHTML)
      };
    }
  }

}


export const svgToDataURL = (svg: string) => {
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

export function getIconSVG(
  icon: string,
  keepEmptyFill?: boolean
): string {
  const domParser = new DOMParser();
  const doc = domParser.parseFromString(icon, 'image/svg+xml');
  // NOTE(mike): IE11 doesn't have the `forEach` method on `NodeList`, therefore
  // need to convert to Array.
  Array.from(doc.querySelectorAll('style')).forEach(element => element.parentNode.removeChild(element));
  Array.from(doc.querySelectorAll('[fill]')).forEach(element => {
    if (keepEmptyFill || element.getAttribute('fill') !== 'none') {
      element.removeAttribute('fill');
    }
  });
  const iconSVG = doc.querySelector('svg');
  // NOTE(mike): The `XMLSerializer` is a workaround for IE11 which doesn't have `outerHTML`
  const iconSVGAsString = iconSVG.outerHTML || new XMLSerializer().serializeToString(iconSVG);
  return iconSVGAsString;
}
